import type { WrittenPage } from '../../sections'

/**
 * "Sustainability as a Strategic Advantage" e-kitabı — Türkçe.
 *
 * Bölüm sırası, görseller ve maddeler İngilizcesiyle birebir aynı; değişen
 * yalnızca nasıl söylendiği. E-kitabın adı kapağında basılı olduğu için
 * İngilizce kalıyor; "compliance", "case study" gibi yerleşik terimler de öyle.
 *
 * İndirilen dosya İngilizce — e-kitabın Türkçesi yok. Bu, sayfayı Türkçe
 * yazmamak için bir sebep değil: sayfayı okuyup indirme kararını veren kişi
 * Türkiye'deki okur, ve karar Türkçe veriliyor.
 *
 * Formla ilgili açık soru İngilizce sayfada duruyor ve orada bir kez duruyor:
 * `check` iki dilde tekrarlanınca aynı karar iki not olarak sayılıyor.
 */
const EBOOK_PDF
  = 'https://njdxtkezxgkrmwwhzcne.supabase.co/storage/v1/object/public/website-documents/ebook/'
  + '3877d853-06ed-4241-9356-54d8e3af9635-Sustainability%20as%20a%20Strategic%20Advantage.pdf'

export const ebookSustainabilityAsAStrategicAdvantage: WrittenPage = {
  navLabel: 'Sustainability as a Strategic Advantage',
  title: 'Sustainability as a Strategic Advantage',
  description:
    'Gizli kalmış iş değerini açığa çıkarmak: sürdürülebilir uygulamalar büyümeyi, inovasyonu ve '
    + 'uzun vadeli başarıyı nasıl besliyor? Digitopia Impact Labs e-kitabını indirin.',
  sections: [
    {
      kind: 'hero',
      eyebrow: 'Gizli kalmış iş değerini açığa çıkarın',
      headline: 'Sustainability as a Strategic Advantage',
      body: [
        'Bugünün rekabet ortamında sürdürülebilirlik bir moda söz değil, stratejik bir zorunluluk. '
          + 'Bu e-kitap, sürdürülebilir uygulamaları işinize katmanın büyümeyi, inovasyonu ve uzun '
          + 'vadeli başarıyı nasıl beslediğini anlatıyor.',
      ],
      ctas: [{ label: 'İndirin', href: EBOOK_PDF }],
      photo: {
        src: '/photo/sustainability-strategic-advantage-ebook-artwork.webp',
        alt:
          'E-kitabın kapak görseli: cam cepheli bir ofis binası ve önünde duran, cepheye yansıyan '
          + 'büyük yeşil bir ağaç.',
      },
      photoFrame: 'square',
    },
    {
      kind: 'cta',
      tone: 'card',
      flip: true,
      heading: 'İçinde ne var?',
      points: [
        'Değişen iş dünyasına dair içgörüler ve sürdürülebilirliğin neden bir zorunluluk olduğu.',
        'Sürdürülebilirliği büyüme ve inovasyon için kullanma stratejileri.',
        'Sürdürülebilir uygulamaların ölçülebilir finansal, operasyonel ve itibar getirisi.',
        'Patagonia ve Unilever gibi sektör liderlerinden gerçek case study’ler.',
        'Sürdürülebilirlik programlarını başlatmak ve ölçmek için pratik adımlar.',
      ],
      ctas: [{ label: 'Raporu indirin', href: EBOOK_PDF }],
      photo: {
        src: '/photo/sustainability-strategic-advantage-ebook-covers.webp',
        alt:
          'Sustainability as a Strategic Advantage e-kitabının iki kopyası, biri diğerinin '
          + 'arkasında, açık renkli bir zeminde.',
      },
    },
    {
      kind: 'cta',
      tone: 'card',
      heading: 'Neden indirmelisiniz?',
      points: [
        'Sürdürülebilirliği bir compliance işi olmaktan çıkarıp stratejik bir üstünlüğe nasıl '
          + 'çevireceğinizi öğrenin.',
        'Şirketinizin dayanıklılığını ve rekabet gücünü artıracak uygulanabilir stratejileri '
          + 'keşfedin.',
        'Sürdürülebilirliği gerçekten işine katmış şirketlerin örneklerinden ilham alın.',
      ],
      ctas: [{ label: 'Raporu indirin', href: EBOOK_PDF }],
      photo: {
        src: '/photo/sustainability-strategic-advantage-ebook-stack.webp',
        alt:
          'Yukarıdan görünen, üst üste dizilmiş Sustainability as a Strategic Advantage e-kitabı '
          + 'kopyaları.',
      },
    },
    {
      kind: 'prose',
      heading: 'Topluluğumuza katılın',
      paragraphs: [
        'Sürdürülebilirlik hızlı ilerliyor ve bunun sizin işiniz için anlamı, yandaki şirket için '
        + 'olduğuyla nadiren aynı. Şirketinizin bugün nerede durduğunu ve bir sonraki adımın neye '
        + 'değeceğini konuşalım.',
      ],
      cta: { label: 'Bize Ulaşın', href: '#contact' },
    },
    {
      kind: 'prose',
      align: 'center',
      heading: 'Kaçırmayın! Ücretsiz e-kitabınızı şimdi indirin',
      sub:
        'Şirketinizi sürdürülebilirlikle dönüştürün. “Sustainability as a Strategic Advantage: '
        + 'Unlocking Hidden Business Value” kopyanızı alın; daha müreffeh ve daha sürdürülebilir '
        + 'bir geleceğe giden yolculuğunuza bugün başlayın.',
      cta: { label: 'E-kitabı indirin', href: EBOOK_PDF },
    },
    {
      kind: 'quotes',
      variant: 'fame',
      heading: 'Müşterilerimiz ne diyor?',
      items: [
        {
          quote:
            'Dijital dönüşüm ve sürdürülebilirlik, bugünün iş dünyasının iki temel önceliği. Bu '
            + 'nedenle nerede olduğumuzu ve nerede olmak istediğimizi değerlendirmek, yönetim '
            + 'ekibimizde ortak bir anlayış ve bağlılık oluşturmak bizim için çok kıymetli. '
            + 'Digitopia’yı, hem dijital hem sürdürülebilirlik dönüşümünün maturity modellerindeki '
            + 'profesyonelliği ve sağlam metodolojisi için seçtik.',
          source: 'Fatma Köktaş, Yönetim Kurulu Üyesi ve CEO',
          company: 'CFN Kimya',
          logo: { name: 'CFN Kimya', src: '/logo/cfnkimya.webp' },
          portrait: { src: '/photo/portrait-fatma-koktas.png', alt: 'Fatma Köktaş' },
        },
      ],
    },
  ],
}
